"use client"
import React, {useRef, useEffect, useState, useCallback} from 'react'
import {ToggleGroup, ToggleGroupItem} from "@/components/ui/toggle-group"
import {cn} from "@/lib/utils"
import {Button} from "@/components/ui/button"
import {ArrowDownWideNarrow, SlidersHorizontal, SortDesc, X} from 'lucide-react'
import {Label} from "@/components/ui/label"
import FilterMobileComponent from "@/components/ui/mobile-filter/filter-mobile-component"
import {Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger} from "@/components/ui/sheet"
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group"
import {useRouter, useSearchParams} from 'next/navigation'
import {useToggleFilterStore} from "@/store/catalog-filter/toggle-filter-store";
import {useTopBarStore} from "@/store/top-bar/useTopBar";
import { AttributeOptionsTitleListType, QueryParamsSearch, VariationOptionsTitleListType } from '@/types/api/search'

const sortOptions = [
    {id: 'DISCOUNT_desc', value: 'DISCOUNT', label: 'بیشترین تخفیف', sort: 'DISCOUNT', sort_flag: 'desc'},
    {id: 'DATE_desc', value: 'DATE', label: 'جدیدترین', sort: 'DATE', sort_flag: 'desc'},
    {id: 'DATE_asc', value: 'DATE', label: 'قدیمی‌ترین', sort: 'DATE', sort_flag: 'asc'},
    {id: 'PRICE_asc', value: 'PRICE', label: 'ارزان‌ترین', sort: 'PRICE', sort_flag: 'asc'},
    {id: 'MOST_ORDER_desc', value: 'MOST_ORDER', label: 'پرفروش‌ترین', sort: 'MOST_ORDER', sort_flag: 'desc'},
    {id: 'MOST_RATE_desc', value: 'MOST_RATE', label: 'محبوب‌ترین', sort: 'MOST_RATE', sort_flag: 'desc'},
    {id: 'MOST_VISITED_desc', value: 'MOST_VISITED', label: 'پربازدیدترین', sort: 'MOST_VISITED', sort_flag: 'desc'},
    {id: 'PRICE_desc', value: 'PRICE', label: 'گران‌ترین', sort: 'PRICE', sort_flag: 'desc'},
]

interface IProps {
    searchParamsValues : QueryParamsSearch
    variationOptionsTitleList : VariationOptionsTitleListType[]
    attributeOptionsTitleList : AttributeOptionsTitleListType[]
}

export default function DynamicSortNav({searchParamsValues , variationOptionsTitleList , attributeOptionsTitleList}: IProps) {
    const {topBarStatus} = useTopBarStore()
    const router = useRouter()
    const searchParams = useSearchParams()
    const navRef = useRef<HTMLDivElement>(null)
    const [isFixed, setIsFixed] = useState(false)
    const [selectedSort, setSelectedSort] = useState<string>(() => {
        const sort = searchParams.get('sort')
        const sort_flag = searchParams.get('sort_flag')
        const option = sortOptions.find(opt => opt.sort === sort && opt.sort_flag === sort_flag)
        return option ? option.id : ''
    })
    const {setOpen, open} = useToggleFilterStore();

    const checkPosition = useCallback(() => {
        if (!navRef.current) return

        const navRect = navRef.current.getBoundingClientRect()
        const productListEnd = document.getElementById('textAfterProduct')?.getBoundingClientRect().bottom
        const scrollPosition = window.scrollY

        if (productListEnd && productListEnd <= window.innerHeight) {
            setIsFixed(false)
        } else {
            setIsFixed(scrollPosition > navRect.top + scrollPosition - navRect.height)
        }
    }, [])

    useEffect(() => {
        checkPosition()
        window.addEventListener('scroll', checkPosition)
        window.addEventListener('resize', checkPosition)

        return () => {
            window.removeEventListener('scroll', checkPosition)
            window.removeEventListener('resize', checkPosition)
        }
    }, [checkPosition])

    const handleSortChange = (id: string) => {
        setSelectedSort(id)
        const option = sortOptions.find(opt => opt.id === id)
        if (option) {
            const params = new URLSearchParams(searchParams.toString())
            params.set('sort', option.sort)
            params.set('sort_flag', option.sort_flag)
            router.push(`?${params.toString()}`)
        }
    }
    return (
        <>
            <div className={"w-full h-12 md:h-auto "} ref={navRef}>
                <nav className="w-full my-7 hidden md:block">
                    <ul className="w-full flex flex-wrap items-center justify-between">
                        <li className="flex items-center justify-start gap-1">
                            <SortDesc className={"w-5 h-5"}/>
                            مرتب سازی بر اساس :
                        </li>

                        <ToggleGroup
                            type="single"
                            value={selectedSort}
                            onValueChange={handleSortChange}
                            className="flex grow-[3] items-center justify-between gap-1"
                        >
                            {sortOptions.map((option) => (
                                <ToggleGroupItem
                                    key={option.id}
                                    value={option.id}
                                    aria-label={`Toggle ${option.label}`}
                                    className={cn(
                                        "font-medium hover:text-orange-primary duration-200 cursor-pointer",
                                        selectedSort === option.id && "text-orange-primary border border-orange-primary"
                                    )}
                                >
                                    {option.label}
                                </ToggleGroupItem>
                            ))}
                        </ToggleGroup>
                    </ul>
                </nav>
                <div
                    className={cn(
                        'md:hidden bg-white duration-200 w-full flex flex-wrap items-center py-2 relative z-20',
                        isFixed ? `fixed border-t ${topBarStatus === "on" ? "top-24" : "top-12"} left-0 right-0  drop-shadow-md menu-show-anime px-[23px]` : 'h-12'
                    )}
                >
                    <Button
                        onClick={() => setOpen(true)}
                        variant="secondary"
                        className="flex items-center justify-start p-1 bg-transparent shadow-none text-black gap-2 text-sm font-bold"
                    >
                        <SlidersHorizontal className="w-6 h-6"/>
                        فیلتر
                    </Button>
                    <SortMobileCatalogPage selectedSort={selectedSort} onSortChange={handleSortChange}/>
                </div>

            </div>
            {open && <FilterMobileComponent
                variationOptionsTitleList={variationOptionsTitleList}
                attributeOptionsTitleList={attributeOptionsTitleList}
                searchParamsValues={searchParamsValues}/>
            }
        </>
    )
}

interface SortMobileCatalogPageProps {
    selectedSort: string
    onSortChange: (value: string) => void
}

const SortMobileCatalogPage: React.FC<SortMobileCatalogPageProps> = ({selectedSort, onSortChange}) => {
    const [open, setOpen] = useState(false)

    const handleSortChange = (id: string) => {
        onSortChange(id)
        setOpen(false)
    }

    const selectedOption = sortOptions.find(option => option.id === selectedSort)
    const selectedLabel = selectedOption?.label || 'جدیدترین'

    return (
        <div>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button variant="secondary"
                            className="flex items-center justify-start p-1 bg-transparent shadow-none text-black gap-1 text-sm font-bold"
                    >
                        <ArrowDownWideNarrow className="w-6 h-6"/>
                        {selectedLabel}
                    </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl backdrop-blur bg-white">
                    <SheetHeader className="text-right">
                        <SheetTitle className="flex items-center justify-start gap-1 font-bold">
                            <Button
                                variant="link"
                                size="icon"
                                className="p-0 m-0"
                                onClick={() => setOpen(false)}
                            >
                                <X className="h-6 w-6"/>
                            </Button>
                            مرتب سازی بر اساس
                        </SheetTitle>
                    </SheetHeader>

                    <RadioGroup
                        value={selectedSort}
                        onValueChange={handleSortChange}
                        className="mt-4 space-y-2"
                    >
                        {sortOptions.map((option) => (
                            <div key={option.id}
                                 className={`flex items-center border-b border-stone-300/50 pb-2 space-x-2 space-x-reverse ${option.id === selectedSort && "text-orange-primary"}`}>
                                <RadioGroupItem value={option.id} id={option.id}/>
                                <Label htmlFor={option.id} className="text-right flex-grow ">
                                    {option.label}
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                </SheetContent>
            </Sheet>
        </div>
    )
}

